import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import {
  CaptureWeightResponse,
  CaptureWeightStateDto,
  GetCapturedWeightStateResponse,
  MeasuredValueDto,
  RejectWeightResponse,
  WeightThresholdCheckResult,
  WeightThresholdDto
} from "~/types/api";

/** Shared weight-threshold state from capture-weight or get-captured-weight-state. */
type WeightCaptureState = {
  weight?: MeasuredValueDto;
  captureWeightState: CaptureWeightStateDto;
};

type AutostorePickingState = {
  currentBatchId: Guid | null;
  pickCompartment: number;
  currentPickQuantity: number;
  weightCaptureResponse: WeightCaptureState | null;
  isWeightEntryModalOpen: boolean;
};

const initialState: AutostorePickingState = {
  currentBatchId: null,
  pickCompartment: 0,
  currentPickQuantity: 0,
  weightCaptureResponse: null,
  isWeightEntryModalOpen: false
};

export const autostorePickingSlice = createSlice({
  name: "autostorePicking",
  initialState,
  reducers: {
    setCurrentBatchId(state, action: PayloadAction<Guid | null>) {
      state.currentBatchId = action.payload;
    },
    setPickCompartment(state, action: PayloadAction<number>) {
      state.pickCompartment = action.payload;
    },
    setCurrentPickQuantity(state, action: PayloadAction<number>) {
      state.currentPickQuantity = action.payload;
    },
    setWeightCaptureResponse(
      state,
      action: PayloadAction<
        | CaptureWeightResponse
        | GetCapturedWeightStateResponse
        | RejectWeightResponse
      >
    ) {
      state.weightCaptureResponse = action.payload;
    },
    resetWeightCaptureResponse(state) {
      state.weightCaptureResponse = null;
      state.isWeightEntryModalOpen = false;
    },
    openWeightEntryModal(state) {
      state.isWeightEntryModalOpen = true;
    },
    closeWeightEntryModal(state) {
      state.isWeightEntryModalOpen = false;
    }
  },
  selectors: {
    selectCurrentBatchId: (state: AutostorePickingState) =>
      state.currentBatchId,
    selectIsWeightEntryModalOpen: (state: AutostorePickingState) =>
      state.isWeightEntryModalOpen,
    selectPickCompartment: (state: AutostorePickingState) =>
      state.pickCompartment,
    selectCurrentPickQuantity: (state: AutostorePickingState) =>
      state.currentPickQuantity,
    selectWeightsCapturedOnPickSoFar: (
      state: AutostorePickingState
    ): MeasuredValueDto[] =>
      state.weightCaptureResponse?.captureWeightState
        ?.allWeightsCapturedOnPickSoFar ?? [],
    selectWeightThresholdCheckResult: (
      state: AutostorePickingState
    ): WeightThresholdCheckResult =>
      state.weightCaptureResponse?.captureWeightState
        ?.weightThresholdCheckResult ?? "WithinThreshold",
    selectSumOfWeightsCapturedOnPickSoFar: (
      state: AutostorePickingState
    ): MeasuredValueDto | undefined =>
      state.weightCaptureResponse?.captureWeightState
        ?.sumOfWeightsCapturedOnPickSoFar,
    selectWeightThresholdLeftToPick: (
      state: AutostorePickingState
    ): WeightThresholdDto | undefined =>
      state.weightCaptureResponse?.captureWeightState?.weightThresholdLeftToPick
  }
});

export const {
  setCurrentBatchId,
  setPickCompartment,
  setCurrentPickQuantity,
  setWeightCaptureResponse,
  resetWeightCaptureResponse,
  openWeightEntryModal,
  closeWeightEntryModal
} = autostorePickingSlice.actions;

export const {
  selectCurrentBatchId,
  selectPickCompartment,
  selectCurrentPickQuantity,
  selectIsWeightEntryModalOpen,
  selectWeightsCapturedOnPickSoFar,
  selectWeightThresholdCheckResult,
  selectSumOfWeightsCapturedOnPickSoFar,
  selectWeightThresholdLeftToPick
} = autostorePickingSlice.selectors;

const autostorePickingPersistConfig = {
  key: "autostorePicking",
  storage,
  whitelist: ["currentBatchId"]
};

export const autostorePickingReducer = persistReducer(
  autostorePickingPersistConfig,
  autostorePickingSlice.reducer
);
